import { FormEvent, useEffect, useState } from 'react';
import { apiGet } from '@shared/api/httpClient';
import type { JobsResponse, ReportJobRecord } from '@shared/types/api';
import { formatLocalDateTime } from '@shared/utils/dateTime';
import { friendlyLabel, friendlyOptionLabel } from '@shared/utils/friendlyLabels';
import { statusPillClassName } from '@/utils/statusPill';

type SearchMode = 'exact' | 'contains' | 'beginsWith';

type JobSearchState = {
  clientId: string;
  jobId: string;
  status: string;
  cmmcLevel: string;
  pipelineName: string;
  search: string;
  searchMode: SearchMode;
  limit: number;
};

const JOB_STATUS_OPTIONS = [
  'ORCHESTRATION_FAILED',
  'WORKSPACES_QUOTA_PREFLIGHT_FAILED',
  'WORKSPACES_QUOTA_PREFLIGHT_SUCCEEDED',
  'WORKSPACES_QUOTA_PREFLIGHT_SKIPPED',
  'ORCHESTRATION_STARTED',
  'REPORT_GENERATION_RUNNING',
  'EVIDENCE_ARCHIVE_REBUILT',
  'EVIDENCE_PACKAGE_UPLOADED',
  'AUDITOR_PACKAGE_READY',
  'DOWNLOAD_LINK_SENT',
  'CLIENT_UPLOAD_REQUESTED',
  'CLIENT_UPLOAD_PENDING',
  'PROCESSED_FINAL_PACKAGE_READY',
  'UPLOADED',
  'PROCESSING',
  'FAILED',
  'ERROR',
];

const CMMC_LEVEL_OPTIONS = ['L1', 'L2', 'L3'];

const DEFAULT_SEARCH: JobSearchState = {
  clientId: '',
  jobId: '',
  status: '',
  cmmcLevel: '',
  pipelineName: '',
  search: '',
  searchMode: 'contains',
  limit: 10,
};

function value(value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ') || '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function rawStatusFor(job: ReportJobRecord): string {
  return value(job.displayStatus || job.jobStatus || job.packageLifecycleStatus || job.lifecycleStatus || job.status);
}

function statusFor(job: ReportJobRecord): string {
  return friendlyLabel(rawStatusFor(job));
}

function formatDate(dateValue: unknown): string {
  return formatLocalDateTime(dateValue);
}

function numberValue(value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
}

function rowKey(job: ReportJobRecord, index: number): string {
  return `${job.clientId || 'client'}-${job.jobId || index}`;
}

function detailRow(label: string, detailValue: unknown) {
  return (
    <div className="detail-row" key={label}>
      <dt>{label}</dt>
      <dd>{value(detailValue)}</dd>
    </div>
  );
}

function detailStatusRow(label: string, detailValue: unknown) {
  return (
    <div className="detail-row" key={label}>
      <dt>{label}</dt>
      <dd><span className={statusPillClassName(detailValue)}>{friendlyLabel(detailValue)}</span></dd>
    </div>
  );
}

function objectValue(value: unknown): Record<string, unknown> | null {
  if (!value) return null;
  if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : null;
    } catch {
      return null;
    }
  }
  return null;
}

function failureReasonFor(job: ReportJobRecord): string {
  const direct = value(job.failureReason);
  if (direct !== '—') return direct;

  const preflight = objectValue(job.preflightResult);
  const preflightMessage = value(preflight?.message);
  if (preflightMessage !== '—') return preflightMessage;

  const errorInfo = objectValue(job.errorInfo);
  const errorMessage = value(errorInfo?.message);
  if (errorMessage !== '—') return errorMessage;

  return '—';
}

function hasFailureDetails(job: ReportJobRecord): boolean {
  return failureReasonFor(job) !== '—' || value(job.preflightStatus) !== '—' || value(job.preflightErrorCode) !== '—';
}

function JobDetails({ job }: { job: ReportJobRecord }) {
  return (
    <article className="card detail-panel">
      <div className="detail-panel-header">
        <div>
          <h3>Job Details</h3>
          <p className="muted-text">{job.clientName || job.clientId || 'Client'} · {job.jobId || 'Job'}</p>
        </div>
        <span className={statusPillClassName(rawStatusFor(job))}>{statusFor(job)}</span>
      </div>

      <div className="detail-grid">
        <section>
          <h4>Summary</h4>
          <dl>
            {detailRow('Client ID', job.clientId)}
            {detailRow('Client Name', job.clientName)}
            {detailRow('Job ID', job.jobId)}
            {detailRow('Job Type', job.jobType)}
            {detailRow('CMMC Level', job.cmmcLevel)}
            {detailRow('Created', formatDate(job.createdAt))}
            {detailRow('Updated', formatDate(job.updatedAt))}
          </dl>
        </section>

        <section>
          <h4>Lifecycle</h4>
          <dl>
            {detailStatusRow('Display Status', job.displayStatus)}
            {detailStatusRow('Status', job.status)}
            {detailStatusRow('Job Status', job.jobStatus)}
            {detailStatusRow('Package Lifecycle', job.packageLifecycleStatus)}
            {detailStatusRow('Client Upload', job.clientUploadStatus)}
            {detailStatusRow('Upload Key Status', job.uploadAccessKeyStatus)}
          </dl>
        </section>

        <section>
          <h4>Readiness</h4>
          <dl>
            {detailRow('PASS', job.finalReadinessPassControls)}
            {detailRow('PARTIAL', job.finalReadinessPartialControls)}
            {detailRow('FAIL', job.finalReadinessFailControls)}
            {detailRow('Not Applicable', job.finalReadinessNotApplicableControls)}
            {detailRow('Resolved', job.finalReadinessResolvedControls)}
            {detailRow('Total', job.finalReadinessTotalControls)}
            {detailRow('Readiness %', job.readinessPercent)}
          </dl>
        </section>

        <section>
          <h4>Package and Delivery</h4>
          <dl>
            {detailRow('Evidence Package', job.evidencePackageS3Uri)}
            {detailRow('Final Auditor Package', job.finalAuditorPackageS3Uri)}
            {detailStatusRow('Delivery Status', job.finalPackageDeliveryStatus)}
            {detailRow('Delivery Channel', job.finalPackageDeliveryChannel)}
            {detailRow('Download Expires', formatDate(job.finalAuditorPackageDownloadUrlExpiresAt))}
          </dl>
        </section>

        <section>
          <h4>Pipeline</h4>
          <dl>
            {detailRow('Pipeline', job.pipelineName)}
            {detailRow('Execution ID', job.pipelineExecutionId)}
            {detailRow('CodeBuild ID', job.codeBuildBuildId)}
            {detailRow('Client Account', job.clientAccountId)}
            {detailRow('Region', job.clientRegion)}
          </dl>
        </section>

        {hasFailureDetails(job) && (
          <section className="detail-section-wide">
            <h4>Preflight / failure details</h4>
            <dl>
              {detailStatusRow('Preflight Status', job.preflightStatus)}
              {detailStatusRow('Preflight Error', job.preflightErrorCode)}
              {detailRow('Failure Reason', failureReasonFor(job))}
            </dl>
          </section>
        )}
      </div>

      <details className="raw-details">
        <summary>Raw record</summary>
        <pre>{JSON.stringify(job, null, 2)}</pre>
      </details>
    </article>
  );
}

export default function JobsPage() {
  const [jobs, setJobs] = useState<ReportJobRecord[]>([]);
  const [selectedJob, setSelectedJob] = useState<ReportJobRecord | null>(null);
  const [search, setSearch] = useState<JobSearchState>(DEFAULT_SEARCH);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [metadata, setMetadata] = useState<{ count?: number; matchedCount?: number; limit?: number; page?: number; pageCount?: number; hasPreviousPage?: boolean; hasNextPage?: boolean } | null>(null);
  const [page, setPage] = useState(1);

  async function loadJobs(criteria: JobSearchState = search, pageNumber = page) {
    setLoading(true);
    setError(null);
    try {
      const response = await apiGet<JobsResponse>('/jobs', {
        query: {
          limit: criteria.limit,
          page: pageNumber,
          clientId: criteria.clientId || undefined,
          jobId: criteria.jobId || undefined,
          status: criteria.status || undefined,
          cmmcLevel: criteria.cmmcLevel || undefined,
          pipelineName: criteria.pipelineName || undefined,
          search: criteria.search || undefined,
          searchMode: criteria.searchMode,
        },
      });
      setJobs(response.jobs ?? []);
      setSelectedJob(null);
      setMetadata({ count: response.count, matchedCount: response.matchedCount, limit: response.limit, page: response.page, pageCount: response.pageCount, hasPreviousPage: response.hasPreviousPage, hasNextPage: response.hasNextPage });
      setPage(response.page ?? pageNumber);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load jobs.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadJobs(DEFAULT_SEARCH, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPage(1);
    void loadJobs(search, 1);
  }

  function handleClear() {
    setSearch(DEFAULT_SEARCH);
    setPage(1);
    void loadJobs(DEFAULT_SEARCH, 1);
  }

  return (
    <div className="page-stack">
      <div className="page-heading">
        <h2>Report Jobs</h2>
        <p>Search report and evidence workflow jobs. Select a row to view the full details below the grid.</p>
      </div>

      <article className="card">
        <h3>Search jobs</h3>
        <form className="form-grid jobs-search-grid" onSubmit={handleSearch}>
          <label>
            Client ID or name
            <input value={search.clientId} onChange={(event) => setSearch({ ...search, clientId: event.target.value })} placeholder="surelinc" />
          </label>
          <label>
            Job ID
            <input value={search.jobId} onChange={(event) => setSearch({ ...search, jobId: event.target.value })} placeholder="2026-05-21T01-10-11Z" />
          </label>
          <label>
            Status
            <select value={search.status} onChange={(event) => setSearch({ ...search, status: event.target.value })}>
              <option value="">All statuses</option>
              {JOB_STATUS_OPTIONS.map((status) => <option key={status} value={status}>{friendlyOptionLabel(status, 'All statuses')}</option>)}
            </select>
          </label>
          <label>
            CMMC Level
            <select value={search.cmmcLevel} onChange={(event) => setSearch({ ...search, cmmcLevel: event.target.value })}>
              <option value="">All levels</option>
              {CMMC_LEVEL_OPTIONS.map((level) => <option key={level} value={level}>{level}</option>)}
            </select>
          </label>
          <label>
            Pipeline Name
            <input value={search.pipelineName} onChange={(event) => setSearch({ ...search, pipelineName: event.target.value })} placeholder="surelinc-cmmc-baseline-pipeline" />
          </label>
          <label>
            Search text
            <input value={search.search} onChange={(event) => setSearch({ ...search, search: event.target.value })} placeholder="download, pipeline, account, package" />
          </label>
          <label>
            Search mode
            <select value={search.searchMode} onChange={(event) => setSearch({ ...search, searchMode: event.target.value as SearchMode })}>
              <option value="contains">Contains</option>
              <option value="exact">Exact word</option>
              <option value="beginsWith">Begins with</option>
            </select>
          </label>
          <label>
            Limit
            <input type="number" min="1" max="100" value={search.limit} onChange={(event) => setSearch({ ...search, limit: Number(event.target.value) || 10 })} />
          </label>
          <div className="form-actions form-wide button-row">
            <button className="primary-button" type="submit" disabled={loading}>{loading ? 'Searching...' : 'Search'}</button>
            <button className="secondary-button" type="button" onClick={handleClear} disabled={loading}>Clear</button>
          </div>
        </form>
      </article>

      <article className="card">
        <div className="grid-header-row">
          <div>
            <h3>Jobs</h3>
            <p className="muted-text">Initial view loads the latest 10 records. Use Next and Previous to page through matched results.</p>
          </div>
          {metadata && <span className="result-count">Showing {jobs.length} of {metadata.matchedCount ?? jobs.length} matched · Page {metadata.page ?? page}{metadata.pageCount ? ` of ${metadata.pageCount}` : ''}</span>}
        </div>

        {loading && <p>Loading jobs...</p>}
        {error && <p className="error-text">{error}</p>}
        {!loading && !error && jobs.length === 0 && <p>No report jobs matched the current filters.</p>}

        {metadata && !loading && !error && (metadata.hasPreviousPage || metadata.hasNextPage) && (
          <div className="pagination-row">
            <button className="secondary-button" type="button" disabled={!metadata.hasPreviousPage || loading} onClick={() => void loadJobs(search, Math.max(1, page - 1))}>Previous</button>
            <span className="muted-text">Page {metadata.page ?? page}{metadata.pageCount ? ` of ${metadata.pageCount}` : ''}</span>
            <button className="secondary-button" type="button" disabled={!metadata.hasNextPage || loading} onClick={() => void loadJobs(search, page + 1)}>Next</button>
          </div>
        )}
        {!loading && !error && jobs.length > 0 && (
          <div className="table-wrap">
            <table className="jobs-table interactive-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Job ID</th>
                  <th>Status</th>
                  <th>Upload</th>
                  <th>CMMC</th>
                  <th>PASS</th>
                  <th>PARTIAL</th>
                  <th>FAIL</th>
                  <th>N/A</th>
                  <th>Updated</th>
                  <th>Delivery</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job, index) => {
                  const selected = selectedJob === job;
                  return (
                    <tr key={rowKey(job, index)} className={selected ? 'selected-row' : undefined} onClick={() => setSelectedJob(job)} tabIndex={0} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') setSelectedJob(job); }}>
                      <td>{value(job.clientName || job.clientId)}</td>
                      <td className="mono-cell">{value(job.jobId)}</td>
                      <td><span className={statusPillClassName(rawStatusFor(job))}>{statusFor(job)}</span></td>
                      <td><span className={statusPillClassName(job.clientUploadStatus)}>{friendlyLabel(job.clientUploadStatus)}</span></td>
                      <td>{value(job.cmmcLevel)}</td>
                      <td>{numberValue(job.finalReadinessPassControls)}</td>
                      <td>{numberValue(job.finalReadinessPartialControls)}</td>
                      <td>{numberValue(job.finalReadinessFailControls)}</td>
                      <td>{numberValue(job.finalReadinessNotApplicableControls)}</td>
                      <td>{formatDate(job.updatedAt)}</td>
                      <td><span className={statusPillClassName(job.finalPackageDeliveryStatus)}>{friendlyLabel(job.finalPackageDeliveryStatus)}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </article>

      {selectedJob && <JobDetails job={selectedJob} />}
    </div>
  );
}
